import queryString from 'query-string'
import { random } from 'lodash'

/**
 * 生成不重复 ID
 * @returns 
 */
export function uuid () {
  const RFC4122_TEMPLATE = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'

  const replacePlaceholders = (placeholder?: string) => {
    let value = random(15)
    // eslint-disable-next-line no-mixed-operators
    value = placeholder === 'x' ? value : (value & 0x3 | 0x8)
    return value.toString(16)
  }

  return RFC4122_TEMPLATE.replace(/[xy]/g, replacePlaceholders)
}

/**
 * 生成后端数据库辨识 ID
 * @returns 
 */
export function guid () {
  const S4 = () => {
    return Math.floor((1 + Math.random()) * 0x10000).toString(16).substring(1)
  }
  return `${S4()}${S4()}-${S4()}-${S4()}-${S4()}-${S4()}${S4()}${S4()}`
}

/**
 * 判断是否符合后端数据库辨识的 ID 格式
 * @param value 
 * @returns 
 */
export function validGUID (value: any): boolean {
  const valid32 = value?.toString().split('-').length === 4 && value.toString().length === 32
  const valid36 = value?.toString().split('-').length === 5 && value.toString().length === 36
  return valid32 || valid36
}

/**
 * 获取 search 查询参数
 * @param str 
 * @returns 
 */
export function getQueryParams (str: string): { [propsName: string]: any } | undefined {
  return str ? queryString.parse(str) : undefined
}

/**
 * 获取 Hash 路由的 search 查询参数
 * @returns 
 */
export function getHashQueryParams (): { [propsName: string]: any } | undefined {
  const { hash } = window.location
  if (hash.indexOf('?') !== -1) {
    return queryString.parse(hash.split('?')[1])
  }
  return
}

/**
 * 获取 location.pathname 参数
 * 比如 /abc/def => ['abc', 'def']
 * @returns 
 */
export function getPathnameParams (): string[] {
  const { pathname } = window.location
  return pathname.split('/').filter(str => !!str)
}

/**
 * 从 a=b 的字符串中获取到 { a: b }
 * @param str 
 * @returns 
 */
export function getEqualStrObject (str: string): { [propsName: string]: any } | undefined {
  return getQueryParams(str)
}

/**
 * 从 a.b 的字符串中获取到字符串 b
 * @param str 
 * @returns 
 */
export function getLastDotString (str: string): string {
  const fieldsArr = str.split('.')
  return fieldsArr[fieldsArr.length - 1]
}

/**
 * 用于判断非0或false的空值
 * @param val 
 * @returns 
 */
export function isEmpty (val: any) {
  return !val && val !== 0 && val !== false
}

/**
 * 获取字符串字长
 * @param str 
 * @returns 
 */
export function getStrLengthByByte (str: string) {
  return typeof TextEncoder === 'undefined' ? String(str).length : (new TextEncoder().encode(str)).length
}

/**
 * 将 mm 单位转换成 px
 * @param mm 
 * @returns 
 */
export function mm2px (mm: number) {
  return mm * 3.7795275591
}

/**
 * 检查 img 是否加载成功
 * @param src
 * @returns 
 */
export function isImageValid (src?: string) {
  if (!src) return Promise.resolve(false)

  return new Promise(resolve => {
    const img = document.createElement('img')
    img.onerror = () => resolve(false)
    img.onload = () => resolve(true)
    img.src = src
  })
}

/**
 * 将上传的 File 图片文件转成 base64 文件
 * @param file 
 * @returns 
 */
export function imgFileToBase64 (file: File): Promise<string> {
  if (/\.(jpe?g|png|gif)$/i.test(file.name)) {
    return new Promise((resolve, reject) => {
      const fr = new FileReader()
      fr.readAsDataURL(file)
      fr.onload = () => {
        resolve(fr.result as string)
      }
      fr.onerror = err => {
        reject(err)
      }
    })
  }
  return Promise.reject(new Error('非图片文件'))
}

/**
 * 将一个字符串解析成 URL 对象，以便获取 origin 等属性
 * @param str 
 * @returns 
 */
export function getStrURL (str: string = ''): URL {
  try {
    return new URL(str)
  } catch (err) {
    return {} as URL
  }
}

/**
 * 是否匹配图片文件
 * @param str 
 * @returns 
 */
export function matchImage (str: string): boolean {
  return (/\.(gif|jpe?g|tiff?|png|webp|bmp)$/i).test(str)
}

/**
 * 是否匹配视频文件
 * @param str 
 * @returns 
 */
export function matchVideo (str: string): boolean {
  return (/\.(mp4|mpe?g|mkv|3gp|avi|mov|wmv|rmvb)$/i).test(str)
}

/**
 * 是否匹配资源文件
 * @param str 
 * @returns 
 */
export function matchWPS (str: string): boolean {
  return (/\.(doc|docx|xls|xlsx|ppt|pptx|pdf)$/i).test(str)
}

/**
 * 是否匹配压缩文件
 * @param str 
 * @returns 
 */
export function matchZIP (str: string): boolean {
  return (/\.(zip|rar|7z)$/i).test(str)
}

/**
 * 是否匹配成文件
 * @param str 
 * @returns 
 */
export function matchFile (str: string): boolean {
  return matchImage(str) || matchVideo(str) || matchWPS(str) || matchZIP(str)
}

/**
 * 创建 a 标签并下载文件
 * @param url 
 */
export function downloadFile (url: string) {
  const anchor = document.createElement('a')
  anchor.setAttribute('download', '')
  anchor.href = url
  anchor.click()
}

/**
 * 获取本地视频时长
 * @param file 
 * @returns 
 */
export function getVideoDuration (file: File): Promise<number> {
  return new Promise((resolve) => {
    const video = document.createElement('video')
    video.preload = 'metadata'
    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src)
      resolve(video.duration)
    }
    video.onerror = () => {
      resolve(0)
    }
    video.src = URL.createObjectURL(file)
  })
}

/**
 * 设置网页 tab 标签标题
 * @param title 
 */
export function setDocTitle (title: string) {
  document.getElementsByTagName('title')[0].innerText = title
}

/**
 * 设置网页 tab 标签图标
 * @param source 
 */
export function setDocIcon (source: string) {
  document.querySelector("link[rel*='icon']")?.setAttribute('href', source)
}